import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import B2bHeader from '../../components/utils/b2bHeader'
import { Box, Chip, IconButton, Table, Tooltip, Typography, Modal, ModalDialog, Button } from '@mui/joy';
import { formatDate } from '../../components/utils';
import { copyToClipboard, truncateString } from '../../utils/HelperFunctions';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../../redux/reducer/loaderSlice';
import { cancelBooking, getAllBookings, issueTicket, refundFlight, resendOtp, viewItinary, viewItinaryWithLogos, voidFlight, hititVoidTicket, hititCancelBooking, hititIssueTicket } from '../../server/api';
import { generatePDFInvoice } from '../../components/Invoice';
import DeleteConfirmation from '../../components/modals/DeleteConfirmation';
import OtpModal from '../../components/OtpModal';
import PnrModal from '../../components/modals/PnrStatus';
import ViewBooking from '../../components/Drawers/ViewBooking';
import PrintIcon from '@mui/icons-material/Print';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { NavbarDivData } from '../../utils/DummyData';
import { NEXT_PUBLIC_PROD_IMAGE_URL } from '../../env';
import AddCardIcon from '@mui/icons-material/AddCard';
import CreditModal from '../../components/modals/CreditModal';
import { generatePDFTicket } from '../../components/utils/Ticket';

const formatApiName = (api) => {
    if (!api) return 'N/A';
    const normalized = api.toLowerCase();
    if (normalized === 'amadus' || normalized === 'amadeus') return 'Amadeus';
    if (normalized === 'hitit') return 'Hitit';
    if (normalized === 'sabre') return 'Sabre';
    return api.charAt(0).toUpperCase() + api.slice(1);
};

const formatFlightType = (flightType) => {
    if (!flightType) return 'N/A';
    return flightType
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getTravelerName = (row) => {
    const traveler = row?.travelers?.[0];
    if (!traveler?.name?.lastName && !traveler?.name?.firstName) return 'N/A';
    return `${traveler.name.lastName || ''}/${traveler.name.firstName || ''}`.replace(/^\/|\/$/g, '') || 'N/A';
};

const getTicketDate = (row) => {
    const api = row?.api?.toLowerCase();

    // Check departure array first (used by Sabre and latest Amadeus/Amadus entries)
    if (row.departure?.[0]?.DepartureDateTime) {
        return formatDate(row.departure[0].DepartureDateTime);
    }

    // Hitit uses departureTime
    if (api === 'hitit' && row.departure?.[0]?.departureTime) {
        return formatDate(row.departure[0].departureTime);
    }

    // Fallback for older Amadeus entries using flightOffers
    const departureAt = row.flightOffers?.[0]?.itineraries?.[0]?.segments?.[0]?.departure?.at;
    if (departureAt) return formatDate(departureAt);

    return 'N/A';
};

const getAirlineCode = (row) => {
    const api = row?.api?.toLowerCase();

    // Check departure array (Sabre, Hitit, and latest Amadeus/Amadus)
    if (row.departure?.[0]) {
        const dep = row.departure[0];

        // MarketingAirline structure (Sabre/Amadeus)
        const airline = dep?.MarketingAirline;
        if (airline?.Code) {
            const flightNo = airline.FlightNumber || dep.FlightNumber;
            return flightNo ? `${airline.Code}-${flightNo}` : airline.Code;
        }

        // marketingCarrier structure (Hitit)
        if (dep?.marketingCarrier) {
            return dep.marketingFlightNumber
                ? `${dep.marketingCarrier}-${dep.marketingFlightNumber}`
                : dep.marketingCarrier;
        }
    }

    // Fallback for older Amadeus entries using flightOffers
    const segment = row.flightOffers?.[0]?.itineraries?.[0]?.segments?.[0];
    if (segment) {
        if (segment.carrierCode && segment.number) {
            return `${segment.carrierCode}-${segment.number}`;
        }
        if (segment.operating?.carrierCode && segment.carrierCode) {
            return `${segment.operating.carrierCode}-${segment.carrierCode}`;
        }
    }

    return 'N/A';
};

const parseBookingListResponse = (res) => {
    if (!res) {
        return { bookings: [], pagination: null, error: 'No response from server' };
    }

    const payload = res.result ?? res.data?.result ?? res.data ?? res;
    let bookings =
        payload?.bookings ??
        payload?.data?.bookings ??
        res.bookings ??
        null;

    if (!bookings && Array.isArray(payload)) {
        bookings = payload;
    }

    if (!bookings && Array.isArray(res.result?.bookings)) {
        bookings = res.result.bookings;
    }

    const pagination =
        payload?.pagination ??
        res.result?.pagination ??
        res.pagination ??
        null;

    const normalizedBookings = Array.isArray(bookings) ? bookings : [];

    if (normalizedBookings.length > 0) {
        return { bookings: normalizedBookings, pagination, error: null };
    }

    if (res.status === 'fail' || res.status === 'error') {
        return {
            bookings: [],
            pagination: null,
            error: res.message || res.error || 'Failed to load bookings',
        };
    }

    return { bookings: normalizedBookings, pagination, error: null };
};

const FlightBooking = () => {
    const [activeDiv, setActiveDiv] = useState(3);
    const [activeOption, setActiveOption] = useState(0);
    const flightBookingRef = useRef({});
    const fetchRequestId = useRef(0);
    const [errors, setErrors] = useState({});
    const agentData = useSelector((state) => state.user.loginUser);
    const [selected, setSelected] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [flightBooking, setFlightBooking] = useState([])
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalBookings, setTotalBookings] = useState(0);
    const [open, setOpen] = useState(false)
    const [openOtpModal, setOpenOtpModal] = React.useState(false);
    const [otp, setOtp] = React.useState(["", "", "", "", "", ""])
    const [openPnrUpdation, setOpenPnrUpdation] = React.useState(false)
    const [state, setState] = React.useState(false);
    const [openCreditModal, setOpenCreditModal] = React.useState({ value: false, pnr: null })

    const isLoading = useSelector((state) => state.loading.loading);
    const agentID = agentData;
    const dispatch = useDispatch()

    const { enqueueSnackbar } = useSnackbar();
    console.log("agentID", agentID);

    const handleInputChange = useCallback((event) => {
        const { name, value } = event.target;
        flightBookingRef.current[name] = value;
    }, []);

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["tripID", "bookingID"];

        requiredFields.forEach((field) => {
            if (!flightBookingRef.current[field]) {
                newErrors[field] = true;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFlightBookingSearch = async () => {
        if (!validateForm()) {
            enqueueSnackbar("Please fill in all required fields.", {
                variant: "error",
            });
            return;
        }

        const body = {
            // agent_ID: agentID
        };


    };

    const fetchAllFlightBookings = async (page = 1, refetch) => {
        const requestId = ++fetchRequestId.current;
        try {
            dispatch(setLoading(true))
            const res = await getAllBookings(page);

            if (requestId !== fetchRequestId.current) return;

            const { bookings, pagination, error } = parseBookingListResponse(res);

            if (error) {
                setFlightBooking([]);
                setTotalPages(1);
                setCurrentPage(1);
                setTotalBookings(0);
                enqueueSnackbar(error, { variant: "error" });
                return;
            }

            setFlightBooking(bookings);
            if (pagination) {
                setTotalPages(pagination.totalPages ?? 1);
                setCurrentPage(pagination.page ?? page);
                setTotalBookings(pagination.total ?? bookings.length);
            } else {
                setTotalPages(1);
                setCurrentPage(1);
                setTotalBookings(bookings.length);
            }

            if (!refetch && bookings.length > 0) {
                enqueueSnackbar("Flight bookings loaded successfully!", { variant: "success" });
            }
        } catch (error) {
            if (requestId !== fetchRequestId.current) return;
            console.error("Error searching flight:", error);
            setFlightBooking([]);
            if (!refetch) {
                enqueueSnackbar(error?.message || String(error), { variant: "error" });
            }
        } finally {
            if (requestId === fetchRequestId.current) {
                dispatch(setLoading(false))
            }
        }
    }

    const bookingList = useMemo(() => {
        if (Array.isArray(flightBooking)) return flightBooking;
        if (Array.isArray(flightBooking?.bookings)) return flightBooking.bookings;
        return [];
    }, [flightBooking]);

    const handlePageChange = (page) => {
        fetchAllFlightBookings(page, true);
    };

    const issueTicketFunction = async (id, apiName, ticketData) => {
        if (agentData?._id === "6a900656e6385e8e4a6c808c" || agentData?.id === "6a900656e6385e8e4a6c808c") {
            enqueueSnackbar("You are restricted from performing this action because this is a demo account.", { variant: "error" });
            return;
        }
        try {
            dispatch(setLoading(true)); // Start loading
            let res;
            if (apiName?.toLowerCase() === "hitit") {
                const hititBody = {
                    orderId: id,
                    ownerCode: "PK",
                    currency: "PKR",
                    paymentFunctions: {
                        paymentProcessingDetails: {
                            amount: `${ticketData?.finalPrice || 22520}`,
                            currency: "PKR",
                            paymentMethod: {
                                accountableDoc: {
                                    docType: "MCO",
                                    ticketId: "4000011567"
                                }
                            },
                            paymentRefID: "PaymentInfo1",
                            typeCode: "MCO"
                        }
                    }
                };
                console.log("Hitit Issue Ticket Body (B2B):", JSON.stringify(hititBody, null, 2));
                res = await hititIssueTicket(hititBody);
            } else {
                const body = { pnr: id };
                res = await issueTicket(body, (apiName === "amadeus" || apiName === "amadus") ? "flights" : apiName);
            }

            // const resData = await viewItinary(id, (ticketData?.api === "amadeus" || ticketData?.api === "amadus") ? "flights" : 'sabre')

            console.log(res, "RES");
            // const agencyLogoUrl = `${NEXT_PUBLIC_PROD_IMAGE_URL}${ticketData?.agencyId?.logo}`;
            // await generatePDFTicket(ticketData, agencyLogoUrl, flightBooking, resData?.result);

            if (apiName?.toLowerCase() === "hitit") {
                if (res?.result?.status === 'success' || res?.code === 200 || res?.status === 'success' || res?.body?.status === 'success') {
                    setOpenOtpModal(false)
                    setOtp(["", "", "", "", "", ""])
                    enqueueSnackbar(`Ticket of PNR: ${id} of ${apiName} has been issued successfully!`, { variant: "success" });
                    setOpenCreditModal({ value: true, pnr: id });
                } else {
                    console.error("Error issuing ticket:", res);
                    const fallbackMsg = `Error issuing ticket for PNR: ${id} of ${apiName}. Please try again later.`;
                    const backendMsg = (typeof res?.message === 'string') ? res.message
                        : (typeof res?.error === 'string') ? res.error
                            : (typeof res?.status === 'string') ? res.status
                                : fallbackMsg;
                    enqueueSnackbar(backendMsg, { variant: "error" });
                    setOpenOtpModal(false)
                    setOtp(["", "", "", "", "", ""])
                }
            } else {
                setOpenOtpModal(false)
                setOtp(["", "", "", "", "", ""])
                enqueueSnackbar(`Ticket of PNR: ${id} of ${apiName} has been issued successfully!`, { variant: "success" });
                setOpenCreditModal({ value: true, pnr: id });
            }

        } catch (error) {
            console.error("Error issuing ticket:", error);
            const fallbackMsg = `Error issuing ticket for PNR: ${id} of ${apiName}. Please try again later.`;
            const backendMsg = (typeof error?.message === 'string') ? error.message
                : (typeof error?.error === 'string') ? error.error
                    : (typeof error === 'string') ? error
                        : fallbackMsg;
            enqueueSnackbar(backendMsg, { variant: "error" });
            setOpenOtpModal(false)
            setOtp(["", "", "", "", "", ""])
        } finally {
            dispatch(setLoading(false)); // Stop loading after the operation is complete
            setOpenOtpModal(false)
            setOtp(["", "", "", "", "", ""])
        }
    }

    const handleOpenOtpModal = async (id, apiName) => {
        const res = await resendOtp({ email: agentData?.email });
        if (res?.message) {
            enqueueSnackbar(res?.message, { variant: "success" });
        }
        setOpenOtpModal({ value: true, flightId: id, flightApi: apiName })
    }

    function isToday(dateString) {
        // Parse the input date string into a Date object
        const inputDate = new Date(dateString);

        // Get today's date
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to the start of today (midnight)

        // Set the input date to the start of the day (ignoring time)
        inputDate.setHours(0, 0, 0, 0);

        // Check if the input date is today
        return inputDate.getTime() === today.getTime();
    }

    useEffect(() => {
        fetchAllFlightBookings(1);
        return () => {
            fetchRequestId.current += 1;
        };
    }, []);

    const handlePrintInvoice = async (invoiceData) => {
        dispatch(setLoading(true))

        // Hit the "old" API first to get basic data for everyone
        let resData = await viewItinaryWithLogos(invoiceData?.id);

        // If it's Sabre, hit the "new" API as well to get the live paymentDeadline
        if (invoiceData?.api?.toLowerCase() === 'sabre') {
            try {
                const liveData = await viewItinary(invoiceData?.id, 'sabre');
                const liveBooking = liveData?.result?.data || liveData?.result;

                if (liveBooking?.paymentDeadline && resData?.message?.booking) {
                    // Extract and format deadline from live data
                    const deadline = liveBooking.paymentDeadline?.expiryDateTime ||
                        (liveBooking.paymentDeadline?.expiryDate && liveBooking.paymentDeadline?.expiryTime ?
                            `${liveBooking.paymentDeadline.expiryDate}T${liveBooking.paymentDeadline.expiryTime}` :
                            liveBooking.paymentDeadline);

                    // Merge live deadline into our main response data
                    resData.message.booking.paymentDeadline = deadline;
                    console.log("[Sabre PDF] Successfully integrated live paymentDeadline:", deadline);
                }
            } catch (err) {
                console.warn("[Sabre PDF] Live viewItinary call failed, using stored data only.", err);
            }
        }

        // Log the raw API response data
        console.log("API Response Data:", resData);
        if (resData?.message) {
            console.log("Message Data:", resData.message);
            console.log("Booking Data:", resData.message.booking);
            console.log("Agency Data:", resData.message.agency);
            console.log("Airline Details:", resData.message.airlineDetails);
        }

        const agencyLogoUrl = resData?.message?.agency?.logoUrl || `${NEXT_PUBLIC_PROD_IMAGE_URL}${invoiceData?.agencyId?.logo}`;

        // Log the agency logo URL
        console.log("Agency Logo URL:", agencyLogoUrl);

        try {
            // Create a compatible structure for the PDF generation
            const formattedData = {
                data: {
                    ...resData?.message?.booking,
                    agency: {
                        agencyName: resData?.message?.agency?.name,
                        agencyEmail: resData?.message?.agency?.email,
                        phoneNumber: resData?.message?.agency?.phone,
                        address: ""
                    },
                    flights: resData?.message?.airlineDetails || [],
                    allSegments: resData?.message?.airlineDetails || [],
                    fares: [{
                        totals: {
                            subtotal: resData?.message?.booking?.orignalPrice,
                            total: resData?.message?.booking?.finalPrice
                        }
                    }],
                    fareOffers: [{
                        cabinBaggageAllowance: {
                            baggagePieces: [{
                                maximumWeightInKilograms: 7
                            }]
                        },
                        checkedBaggageAllowance: {
                            totalWeightInKilograms: 20
                        }
                    }]
                }
            };

            // Log the formatted data
            console.log("Formatted Data for PDF:", formattedData);

            await generatePDFTicket(invoiceData, resData, agencyLogoUrl, formattedData);
            dispatch(setLoading(false))
            enqueueSnackbar("Invoice PDF generated successfully", { variant: "success" });
        } catch (error) {
            dispatch(setLoading(false))
            console.error("Error generating PDF:", error);
            enqueueSnackbar("Error generating PDF invoice: " + error.message, { variant: "error" });
        }
    };

    const handleDelete = async (flight) => {
        try {
            dispatch(setLoading(true))
            let res;
            let statusName;
            if (flight?.status === 'confirmed') {
                if (flight?.api?.toLowerCase() === 'hitit') {
                    const voidBody = {
                        orderId: flight?.id,
                        ticketDocNbr: flight?.tickets?.[0]?.documentNumber
                    };
                    res = await hititVoidTicket(voidBody);
                    statusName = 'Voided';
                } else if (isToday(flight?.createdAt)) {
                    const isAmadeus = flight?.api?.toLowerCase() === 'amadeus' || flight?.api?.toLowerCase() === 'amadus';
                    if (isAmadeus) {
                        // Use refundFlight for Amadeus as the backend deleteBooking expects { bookingId }
                        res = await refundFlight(flight?.id, 'amadeus');
                    } else {
                        res = await voidFlight(flight?.id, flight?.api === 'Sabre' ? 'sabre' : 'flights');
                    }
                    statusName = 'Voided';
                } else {
                    const isAmadeus = flight?.api?.toLowerCase() === 'amadeus' || flight?.api?.toLowerCase() === 'amadus';
                    const url = isAmadeus ? 'amadeus' : (flight?.api === 'Sabre' ? 'sabre' : 'flights');
                    res = await refundFlight(flight?.id, url);
                    statusName = 'Refunded';
                }
                dispatch(setLoading(false))
            } else if (flight?.status === "hold" || flight?.status === "voided") {
                if (flight?.api?.toLowerCase() === 'hitit') {
                    const cancelBody = {
                        orderId: flight?.id,
                        action: "COMMIT",
                        ownerCode: "PK",
                        currency: "PKR"
                    };
                    res = await hititCancelBooking(cancelBody);
                } else {
                    const isAmadeus = flight?.api?.toLowerCase() === 'amadeus' || flight?.api?.toLowerCase() === 'amadus';
                    const url = isAmadeus ? 'amadeus' : (flight?.api === 'Sabre' ? 'sabre' : 'flights');
                    res = await cancelBooking(flight?.id, url);
                }
                statusName = 'Cancelled'
                dispatch(setLoading(false))
            } else {
                enqueueSnackbar('Flight Status are not available', { variant: "error" });
                dispatch(setLoading(false))
                return;
            }
            if (res?.status === "success") {
                enqueueSnackbar(`Flight Booking ${statusName} Successful!`, { variant: "success" });
                fetchAllFlightBookings(currentPage, true)
                dispatch(setLoading(false))
            } else {
                enqueueSnackbar(res?.status || 'Something went wrong 1', { variant: "error" });
                dispatch(setLoading(false))
            }
        } catch (e) {
            console.log(e)
            const fallbackMsg = `Something went wrong!`;
            const backendMsg = (typeof e?.message === 'string') ? e.message
                : (typeof e?.error === 'string') ? e.error
                    : (typeof e === 'string') ? e
                        : fallbackMsg;
            enqueueSnackbar(backendMsg, { variant: "error" });
            dispatch(setLoading(false))
        }

        setOpen(false)
    }

    const handleOpenPnrStatusModal = () => {
        setOpenPnrUpdation(true)
    }

    const toggleDrawer = (open, id, apiName) => async (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }

        if (!open) {
            setState(false);
            return;
        }

        setState({ value: open, id, api: apiName })
    };

    function RowMenu({ flight, setOpen, setOpenOtpModal }) {
        console.log(flight, '******************')
        return (
            <>
                <Tooltip
                    arrow={false}
                    color="neutral"
                    size="md"
                    variant="solid"
                    title="View Detail"
                >
                    <RemoveRedEyeIcon onClick={toggleDrawer(true, flight.id, flight.api)} sx={{ fontSize: '25px', cursor: 'pointer', marginRight: '5px' }} />
                </Tooltip>

                {!['canceled', 'voided']?.includes(flight?.status) && (
                    <Tooltip
                        arrow={false}
                        color="neutral"
                        size="md"
                        variant="solid"
                        title="Print Invoice"
                    >
                        <PrintIcon onClick={() => handlePrintInvoice(flight)} sx={{ fontSize: '25px', cursor: 'pointer', mr: 1 }} />
                    </Tooltip>
                )}

                {!['canceled', 'voided', 'confirmed']?.includes(flight?.status) && (
                    <Tooltip
                        arrow={false}
                        color="neutral"
                        size="md"
                        variant="solid"
                        title="Issue Ticket"
                    >
                        <ReceiptLongIcon onClick={() => issueTicketFunction(flight.id, flight.api, flight)} sx={{ fontSize: '25px', cursor: 'pointer', mr: 1 }} />
                    </Tooltip>
                )}

                {['superadmin', 'agency'].includes(agentData?.role?.toLowerCase()) && !["6a394f52e83dff59085ef657", "6a44f54763d81a9af4be47c1", "6a0ee0d63a89f33767c3d2ae"].includes(agentData?._id) && !["6a394f52e83dff59085ef657", "6a44f54763d81a9af4be47c1", "6a0ee0d63a89f33767c3d2ae"].includes(agentData?.id) && flight?.status && flight?.status !== "canceled" && (
                    <Tooltip
                        arrow={false}
                        color="danger"
                        size="md"
                        variant="solid"
                        title={`${flight?.status === 'confirmed' ? 'Void' : flight?.status === 'voided' || flight?.status === 'refunded' || flight?.status === 'hold' ? 'Cancel' : flight?.status === 'confirmed' ? 'Void' : 'N/A'} Booking`}
                    >
                        <EventBusyIcon onClick={() => { setOpen({ modal: true, flightId: flight?._id, api: flight?.api, flight }) }} sx={{ fontSize: '25px', cursor: 'pointer' }} />
                    </Tooltip>
                )}

                {['confirmed']?.includes(flight?.status) && (
                    <Tooltip
                        arrow={false}
                        color="neutral"
                        size="md"
                        variant="solid"
                        title="Add Credit"
                    >
                        <AddCardIcon onClick={() => setOpenCreditModal({ value: true, pnr: flight.id, isEdit: true })} sx={{ fontSize: '25px', cursor: 'pointer', mr: 1 }} />
                    </Tooltip>
                )}
            </>
        );
    }

    return (
        <>
            <B2bHeader divs={NavbarDivData} activeDiv={activeDiv} setActiveDiv={setActiveDiv} activeOption={activeOption} setActiveOption={setActiveOption} showDiv={true} hidePromotion={true} />

            <Box sx={{ margin: 3 }}>
                <Table
                    aria-labelledby="tableTitle"
                    // stickyHeader
                    hoverRow
                    sx={{
                        "--TableCell-headBackground":
                            "var(--joy-palette-background-level1)",
                        "--Table-headerUnderlineThickness": "1px",
                        "--TableRow-hoverBackground":
                            "var(--joy-palette-background-level1)",
                        "--TableCell-paddingY": "4px",
                        "--TableCell-paddingX": "8px",
                    }}
                >
                    <thead>
                        <tr>
                            {/* <th>
                <Checkbox
                  size="sm"
                  indeterminate={
                    selected.length > 0 && selected.length !== agencies.length
                  }
                  checked={selected.length === agencies.length}
                  onChange={(event) =>
                    setSelected(
                      event.target.checked ? agencies.map((row) => row.id) : []
                    )
                  }
                />
              </th> */}
                            <th style={{ textAlign: 'center' }}>ID</th>
                            <th style={{ textAlign: 'center' }}>Traveller Name</th>
                            <th style={{ textAlign: 'center' }}>Price</th>
                            <th style={{ textAlign: 'center' }}>Ticket Date</th>
                            <th style={{ textAlign: 'center' }}>Booking Date</th>
                            <th style={{ textAlign: 'center' }}>Airline Code</th>
                            <th style={{ textAlign: 'center' }}>API</th>
                            <th style={{ textAlign: 'center' }}>Flight Type</th>
                            {/* <th style={{ textAlign: 'center' }}>Booking Type</th> */}
                            <th style={{ textAlign: 'center' }}>Status</th>
                            <th style={{ textAlign: 'center' }}>PNR</th>
                            <th style={{ textAlign: 'center' }}>Action</th>



                        </tr>
                    </thead>
                    {bookingList.length > 0 ? (
                        <tbody>
                            {bookingList.map(
                                (row) => (
                                    <tr key={row._id}>
                                        {/* <td>
                      <Checkbox
                        size="sm"
                        checked={selected.includes(row.userName)}
                        onChange={(event) =>
                          setSelected(
                            event.target.checked
                              ? [...selected, row.userName]
                              : selected.filter((name) => name !== row.userName)
                          )
                        }
                      />
                    </td> */}
                                        <td style={{ textAlign: 'center', cursor: 'pointer', display: 'flex', justifyContent: row.id ? 'space-between' : 'center', alignItems: 'center' }}>
                                            <Tooltip title={row._id} variant="solid" sx={{ marginRight: '10px' }}>
                                                <span>{row._id?.slice(-6)}</span>
                                            </Tooltip>
                                            <ContentCopyIcon sx={{ marginLeft: '10x', cursor: 'pointer' }} onClick={() => copyToClipboard(row._id, 'Booking ID')} />
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{getTravelerName(row)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {row?.finalPrice
                                                ? `RS. ${row?.api?.toLowerCase() === 'hitit'
                                                    ? (parseFloat(row.finalPrice)).toFixed(2)
                                                    : row.finalPrice}`
                                                : 'N/A'}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{getTicketDate(row)}</td>
                                        <td style={{ textAlign: 'center' }}>{row.createdAt ? formatDate(row.createdAt) : 'N/A'}</td>
                                        <td style={{ textAlign: 'center' }}>{getAirlineCode(row)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <Chip color="neutral" variant="soft">
                                                {formatApiName(row?.api)}
                                            </Chip>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {row?.flightType ? (
                                                <Chip color="primary" variant="outlined">
                                                    {formatFlightType(row.flightType)}
                                                </Chip>
                                            ) : 'N/A'}
                                        </td>
                                        {/* <td style={{ textAlign: 'center' }}>
                                            {row?.bookingType ? (
                                                <Chip
                                                    color={row.bookingType === "online" ? "primary" : "neutral"}
                                                    variant="solid"
                                                >
                                                    {row.bookingType === "online" ? "Online" : "Offline"}
                                                </Chip>
                                            ) : 'N/A'}
                                        </td> */}
                                        <td style={{ textAlign: 'center' }}>{row?.status === "hold" ? <Chip
                                            color="warning"
                                            variant="solid"
                                        >
                                            Hold
                                        </Chip> : row?.status === "canceled" ? <Chip
                                            color="danger"
                                            variant="solid"
                                        >
                                            Cancelled
                                        </Chip> : row?.status === "voided" ? <Chip
                                            color="danger"
                                            variant="solid"
                                        >
                                            Voided
                                        </Chip> : row?.status === "confirmed" ? <Chip
                                            color="success"
                                            variant="solid"
                                        >
                                            Issued
                                        </Chip> : row?.status === "refunded" ? <Chip
                                            color="danger"
                                            variant="solid"
                                        >
                                            Refunded
                                        </Chip> : <Chip
                                            color="warning"
                                            variant="solid"
                                        >
                                            N/A
                                        </Chip>}</td>
                                        <td style={{ textAlign: 'center', display: 'flex', justifyContent: row.id ? 'space-between' : 'center', alignItems: 'center' }}>
                                            {(row?.api === "amadeus" || row?.api === "amadus") && row?.reference ? truncateString(row.reference, 17) : (row?.api === "Sabre" || row?.api?.toLowerCase() === "hitit") && row?.id ? truncateString(row.id, 17) : 'N/A'}
                                            {((row?.api === "amadeus" || row?.api === "amadus") && row?.reference || (row?.api === "Sabre" || row?.api?.toLowerCase() === "hitit") && row?.id) && (
                                                <ContentCopyIcon sx={{ marginLeft: '5x', cursor: 'pointer' }} onClick={() => copyToClipboard((row?.api === "amadeus" || row?.api === "amadus") && row?.reference ? row?.reference : (row?.api === "Sabre" || row?.api?.toLowerCase() === "hitit") && row?.id ? row.id : null, 'PNR number')} />
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <RowMenu flight={row} setOpen={setOpen} setOpenOtpModal={setOpenOtpModal} />
                                        </td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    ) : null}
                </Table>

                {bookingList.length === 0 && (
                    <Typography level="h4" sx={{ textAlign: 'center', mt: 4 }}>
                        No Data Found!
                    </Typography>
                )}

                {totalPages > 1 && (
                    <Box
                        sx={{
                            pt: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                            Showing page {currentPage} of {totalPages} ({totalBookings} bookings)
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center">
                            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                                (page) => (
                                    <IconButton
                                        key={page}
                                        size="sm"
                                        variant={page === currentPage ? "solid" : "outlined"}
                                        onClick={() => handlePageChange(page)}
                                        sx={{
                                            color: page === currentPage ? "#ffffff" : "#185ea5",
                                            borderColor: "#185ea5",
                                            borderWidth: 1,
                                            backgroundColor:
                                                page === currentPage ? "#185ea5" : "transparent",
                                            "&:hover": {
                                                backgroundColor: "#185ea5",
                                                color: "#ffffff",
                                                borderColor: "#185ea5",
                                            },
                                        }}
                                    >
                                        {page}
                                    </IconButton>
                                )
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            {open?.modal && (open?.api?.toLowerCase() === 'amadeus' || open?.api?.toLowerCase() === 'amadus') && ['super_admin', 'superadmin', 'agency'].includes(agentData?.role?.toLowerCase()) && (
                <Modal open={true} onClose={() => setOpen(false)}>
                    <ModalDialog>
                        <Typography level="h3" mb={2}>
                            Cancelled and Void Ticket
                        </Typography>
                        <Typography mb={3}>
                            For cancel the booking or voiding the ticket please use Amadeus manual system.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button loading={isLoading} variant="solid" color="danger" onClick={() => handleDelete(open?.flight)}>
                                Mark as Cancelled and Voided
                            </Button>
                            <Button disabled={isLoading} variant="outlined" color="neutral" onClick={() => setOpen(false)}>
                                Close
                            </Button>
                        </Box>
                    </ModalDialog>
                </Modal>
            )}

            {open?.modal && (!(open?.api?.toLowerCase() === 'amadeus' || open?.api?.toLowerCase() === 'amadus') || !['super_admin', 'superadmin', 'agency'].includes(agentData?.role?.toLowerCase())) && (
                <DeleteConfirmation open={open?.modal} setOpen={setOpen} handleDelete={handleDelete} description={`Are you sure you want to ${open?.flight?.status === "hold" || open?.flight?.status === 'confirmed' || open?.flight?.status === 'voided' ? 'cancelled' : 'N/A'} this booking?`} flight={open?.flight} />
            )}
            {/* <OtpModal {...{ open: openOtpModal?.value, setOpen: setOpenOtpModal, flightId: openOtpModal?.flightId, apiName: openOtpModal?.flightApi, handleSubmit: issueTicketFunction, otp, setOtp, email: agentData?.email }} /> */}
            <PnrModal {...{ open: openPnrUpdation, setOpen: setOpenPnrUpdation, refetch: () => fetchAllFlightBookings(currentPage, true) }} />
            {state?.value && (
                <ViewBooking {...{ state: state?.value, setState, toggleDrawer, id: state?.id, api: state?.api }} />
            )}

            {openCreditModal?.value && (
                <CreditModal {...{ open: openCreditModal?.value, setOpen: setOpenCreditModal, pnr: openCreditModal?.pnr, isEdit: openCreditModal?.isEdit }} />
            )}
        </>
    )
}

export default FlightBooking